import {AfterViewInit, Component, Input, OnChanges, OnInit, SimpleChanges, TemplateRef, ViewChild} from '@angular/core';
import {
    DefaultGridButtons,
    GridColumn,
    MxButtonModel,
    MxButtonModule,
    MxCustomizer,
    MxGridButtonsConfig,
    MxGridCustomizerEvent,
    MxGridModule,
    MxGridService
} from 'mx-core';
import {PrlTranslateService} from '../../../../shared/services/prl-translate.service';
import {PromoMxObj, ZoneLightPrlObj} from '../../../../../../generated/generated.model';
import {PrlZoneViewComponent} from '../prl-zone-view/prl-zone-view.component';
import {PrlGenerateCodeComponent} from '../prl-generate-code/prl-generate-code.component';
import {MatDialog} from '@angular/material/dialog';

type PrlZoneGridRow = ZoneLightPrlObj & {
    promo?: PromoMxObj;
    name?: string;
    thumbContent?: PromoMxObj;
    typeName?: string;
    subTypeName?: string;
    productName?: string;
    languageCode?: string;
    currencyCode?: string;
    zone_description?: string;
};

@Component({
    selector: 'prl-zones-list',
    standalone: true,
    imports: [MxGridModule, MxButtonModule, PrlZoneViewComponent],
    styleUrl: './prl-zones-list.component.scss',
    templateUrl: './prl-zones-list.component.html'
})
export class PrlZonesListComponent implements OnInit, OnChanges, AfterViewInit, MxCustomizer<MxGridCustomizerEvent> {

    @Input() lightZones: ZoneLightPrlObj[] = [];

    @ViewChild('promoView', {static: true})
    private promoViewTemplate!: TemplateRef<any>;

    @ViewChild('generateCodeCell', {static: true})
    private generateCodeCellTemplate!: TemplateRef<any>;

    zoneRows: PrlZoneGridRow[] = [];
    generateCodeBtn: MxButtonModel = {
        label: () => this.translateService.getTranslatedLabel('label.marketing.tools.generate.code'),
        tooltip: () => this.translateService.getTranslatedLabel('label.marketing.tools.generate.code'),
        className: 'mxs-button--secondary'
    };
    private gridService!: MxGridService;
    private columns: GridColumn[] = [];

    constructor(
        private readonly dialog: MatDialog,
        private readonly translateService: PrlTranslateService) {
    }

    ngOnInit(): void {
        this.initColumns();
        this.collectZoneRows();
    }

    ngAfterViewInit(): void {
        this.assignCellTemplates();
        this.gridService?.columns.setColumns(this.columns);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.lightZones && changes.lightZones.currentValue !== undefined) {
            this.collectZoneRows();
            this.gridService?.collection.setRows(this.zoneRows);
            this.gridService?.collection.rowsChanged();
        }
    }

    onCustomize(event: MxGridCustomizerEvent): void {
        event.customizeColumns(this.columns)
            .customizeLoadRowsOnStart(true)
            .customizeActions(new MxGridButtonsConfig([
                [DefaultGridButtons.add, {hidden: true}],
                [DefaultGridButtons.edit, {hidden: true}],
                [DefaultGridButtons.remove, {hidden: true}],
                [DefaultGridButtons.view, {hidden: true}],
                [DefaultGridButtons.reload,{hidden: true}],
            ]))
            .customizeProps({
                showButtons: false,
                selectionMode: 'single',
                sortDirection: 'asc'
            })
            .customizeRowsSource(this.zoneRows.slice())
            .onSetUp(service => {
                this.gridService = service;

                this.assignCellTemplates();

                this.gridService.columns.setColumns(this.columns);
            });
    }

    generateCodeForZone(zone: ZoneLightPrlObj): void {
        this.dialog.open(PrlGenerateCodeComponent, {
            width: '80%',
            panelClass: 'mx-dialog-panel-class',
            data: {
                zone
            }
        }).afterClosed();
    }

    private assignCellTemplates(): void {
        this.columns
            .filter(col => col.isThumbnail)
            .forEach(col => col.cellTemplate = this.promoViewTemplate);

        this.columns
            .filter(col => col.prop === 'generateCode')
            .forEach(col => col.cellTemplate = this.generateCodeCellTemplate);
    }

    private initColumns(): void {
        this.columns = [
            new GridColumn({
                field: 'id',
                label: 'ID',
                hidden: true,
                sortable: true
            }),
            new GridColumn({
                field: 'generateCode',
                sortable: false,
                columnWidth: 160,
                cellTemplate: this.generateCodeCellTemplate,
                label: () => this.translateService.getTranslatedLabel('label.marketing.tools.generate.code')
            }),
            new GridColumn({
                field: 'thumbContent',
                columnWidth: 120,
                isThumbnail: true,
                hidden: false,
                sortable: false,
                cellTemplate: this.promoViewTemplate,
                label: () => this.translateService.getTranslatedLabel('label.marketing.tools.zone.preview')
            }),
            new GridColumn({
                field: 'typeName',
                label: () => this.translateService.getTranslatedLabel('label.marketing.tools.zone.creative')
            }),
            new GridColumn({
                field: 'subTypeName',
                label: () => this.translateService.getTranslatedLabel('label.marketing.tools.zone.type')
            }),
            new GridColumn({
                field: 'description',
                label: () => this.translateService.getTranslatedLabel('label.marketing.tools.zone.description')
            }),
            new GridColumn({
                field: 'zone_description',
                label: () => {
                    const label1 = this.translateService.getTranslatedLabel('label.marketing.tools.zone.creative');
                    const label2 = this.translateService.getTranslatedLabel('label.marketing.tools.zone.description');
                    return `${label1} ${label2}`;
                }
            }),
            new GridColumn({
                field: 'productName',
                label: () => this.translateService.getTranslatedLabel('label.marketing.tools.zone.brand')
            }),
            new GridColumn({
                field: 'languageCode',
                label: () => this.translateService.getTranslatedLabel('label.marketing.tools.zone.languages')
            }),
            new GridColumn({
                field: 'currencyCode',
                label: () => this.translateService.getTranslatedLabel('label.marketing.tools.zone.currencies')
            }),
        ];
    }

    private collectZoneRows(): void {
        this.zoneRows = (this.lightZones || [])
            .filter(zone => !!zone.promos?.length)
            .map(zone => {
            const promo = zone.promos?.[0];

            return {
                ...zone,
                promo,
                thumbContent: promo,
                name: promo?.name,
                typeName: promo?.typeName,
                subTypeName: promo?.subTypeName,
                description: zone.description,
                zone_description: promo?.description,
                productName: promo?.productName,
                languageCode: promo?.languageCode,
                currencyCode: promo?.currencyCode,
            };
        });
    }
}
